import { startCase } from 'lodash';

import moment from 'moment';

import PropTypes from 'prop-types';

import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

import CustomDatePicker from 'atoms/CustomDatePicker';

const toDate = (value) => (value ? moment(value).toDate() : null);

const newPeriod = () => ({
	id: null,
	startDate: new Date(),
	endDate: null,
	isDeleted: false,
});

const toPeriods = (enrollments) =>
	Array.isArray(enrollments) && enrollments.length > 0
		? enrollments.map((enrollment) => ({
				id: enrollment.id,
				startDate: toDate(enrollment.startDate),
				endDate: toDate(enrollment.endDate),
				isDeleted: false,
		  }))
		: [newPeriod()];

/**
 * Edits every enrollment instance a client holds in one or more groups. A client can
 * enrol, leave and re-enrol, so each group shows its full history regardless of which
 * row opened the modal. Mirrors ClinicianTenureModal.
 */
const UpdateEnrollmentModal = ({ isOpen, toggle, title, client, groups, onSave }) => {
	const [groupPeriods, setGroupPeriods] = useState([]);

	useEffect(() => {
		setGroupPeriods(
			groups.map((group) => ({
				programId: group.programId,
				groupName: group.groupName,
				periods: toPeriods(group.enrollments),
			})),
		);
	}, [groups, isOpen]);

	const updatePeriod = (groupIndex, periodIndex, changes) => {
		setGroupPeriods((current) =>
			current.map((group, gi) =>
				gi === groupIndex
					? {
							...group,
							periods: group.periods.map((period, pi) =>
								pi === periodIndex ? { ...period, ...changes } : period,
							),
					  }
					: group,
			),
		);
	};

	const handleDeletePeriod = (groupIndex, periodIndex) => {
		setGroupPeriods((current) =>
			current.map((group, gi) => {
				if (gi !== groupIndex) return group;

				// Saved periods stay in the list flagged for deletion so the API is told
				// to remove them; unsaved ones can just disappear.
				return {
					...group,
					periods: group.periods[periodIndex].id
						? group.periods.map((period, pi) =>
								pi === periodIndex ? { ...period, isDeleted: true } : period,
						  )
						: group.periods.filter((period, pi) => pi !== periodIndex),
				};
			}),
		);
	};

	const handleAddPeriod = (groupIndex) => {
		setGroupPeriods((current) =>
			current.map((group, gi) =>
				gi === groupIndex ? { ...group, periods: [...group.periods, newPeriod()] } : group,
			),
		);
	};

	// A period the user can still see but has left without a start date blocks the
	// save. Deleted periods are exempt — they are on their way out.
	const isIncomplete = (period) => !period.isDeleted && !period.startDate;

	const hasIncompletePeriod = groupPeriods.some((group) =>
		group.periods.some(isIncomplete),
	);

	const handleSave = () => {
		// The Save button is disabled in this state; belt and braces for any other caller.
		if (hasIncompletePeriod) {
			return;
		}

		const enrollments = groupPeriods.flatMap((group) =>
			group.periods
				// Deleted periods that were never saved have already been dropped.
				.map((period) => ({
					id: period.id,
					programId: group.programId,
					startDate: period.startDate
						? moment(period.startDate).format('YYYY-MM-DD')
						: null,
					endDate: period.endDate ? moment(period.endDate).format('YYYY-MM-DD') : null,
					delete: period.isDeleted,
				})),
		);

		onSave(enrollments);
	};

	const renderPeriod = (group, groupIndex, periodIndex) => {
		const period = group.periods[periodIndex];
		const inputId = `${group.programId || groupIndex}-${periodIndex}`;

		return (
			<Row key={inputId} className="mb-3 align-items-end">
				{/*
				  mb-3 mb-md-0 on both pickers' columns: at >=md the three Cols sit
				  side by side and the message below hangs in the Row's own mb-3
				  gutter, but .col-md-5 has no vertical margin under 768px, so there
				  they stack flush and the thing directly below the START column is
				  the END column's label. mb-md-0 wins at >=md (same specificity,
				  later in bootstrap's source), so this is inert at desktop widths.
				*/}
				<Col md={5} className="mb-3 mb-md-0">
					{/*
					  The message hangs in that gutter instead of sitting in the layout
					  flow. The row is align-items-end, so a message that takes up height
					  pushes the END picker and the trash button down with it and grows
					  the modal — the shift UAT asked us to stop (2026-09-01). Same
					  overlay trick as ShareFormModal's ".pdf" suffix and
					  CustomDatePicker's own clear button. The box stays mounted and
					  empty while the period is fine, so role="alert" speaks only when
					  the text actually lands.
					*/}
					<div style={{ position: 'relative' }}>
						<CustomDatePicker
							label={<span className="form-group">START DATE</span>}
							id={`enrollment-start-${inputId}`}
							selected={period.startDate}
							handleDateChange={(date) =>
								updatePeriod(groupIndex, periodIndex, { startDate: date })
							}
							placeholder="Select Date to Start"
						/>
						<div
							className="text-danger font-11"
							id={`enrollment-start-error-${inputId}`}
							role="alert"
							style={{
								position: 'absolute',
								top: '100%',
								left: 0,
								marginTop: 2,
								// Unitless, because .font-11 is 0.6875rem and
								// global-styles.css pins the root font to 16px below
								// 1200px and 19px at/above it: the text is 11px or
								// 13.1px, and a hardcoded px line box would be smaller
								// than the glyphs on desktop. 2 + 1.1x gives 14.1px /
								// 16.4px, each inside the 1rem gutter it hangs in
								// (16px / 19px at the same two breakpoints).
								lineHeight: 1.1,
								whiteSpace: 'nowrap',
								pointerEvents: 'none',
							}}
						>
							{isIncomplete(period) ? 'Start date is required' : ''}
						</div>
					</div>
				</Col>
				<Col md={5} className="mb-3 mb-md-0">
					<CustomDatePicker
						label={<span className="form-group">END DATE</span>}
						id={`enrollment-end-${inputId}`}
						selected={period.endDate}
						handleDateChange={(date) =>
							updatePeriod(groupIndex, periodIndex, { endDate: date })
						}
						minDate={period.startDate}
						isClearable
						placeholder="Active indefinitely"
					/>
				</Col>
				<Col md={2}>
					<Button
						color="link"
						className="text-danger"
						onClick={() => handleDeletePeriod(groupIndex, periodIndex)}
						title="Delete enrollment period"
					>
						<DeleteIcon fontSize="small" />
					</Button>
				</Col>
			</Row>
		);
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="lg">
			<ModalHeader toggle={toggle}>
				{title || `${startCase(client.name)} - Enrollment History`}
			</ModalHeader>
			<ModalBody>
				{groupPeriods.map((group, groupIndex) => {
					const visiblePeriods = group.periods
						.map((_, periodIndex) => periodIndex)
						.filter((periodIndex) => !group.periods[periodIndex].isDeleted);

					return (
						<div key={group.programId || groupIndex} className="mb-3">
							{group.groupName && (
								<div className="my-auto ml-1 mb-2 font-weight-bold">
									{group.groupName}
								</div>
							)}
							{visiblePeriods.length === 0 ? (
								<p className="text-muted">
									No enrollment periods. Add one or the client will be removed
									from this group.
								</p>
							) : (
								visiblePeriods.map((periodIndex) =>
									renderPeriod(group, groupIndex, periodIndex),
								)
							)}
							<Button
								color="link"
								onClick={() => handleAddPeriod(groupIndex)}
								className="mt-2"
							>
								<AddIcon fontSize="small" style={{ marginRight: 4 }} /> Add
								Enrollment Period
							</Button>
						</div>
					);
				})}
			</ModalBody>
			<div className="d-flex justify-content-end align-items-center mb-3 px-3">
				<button
					type="button"
					className="btn btn-primary btn-border btn-color-cust mr-2 px-5 py-3 text-uppercase font-11 link-color"
					onClick={toggle}
				>
					Cancel
				</button>
				<button
					type="button"
					className="btn btn-primary btn-border primary-btn-color px-5 py-3 text-uppercase font-11"
					onClick={handleSave}
					disabled={hasIncompletePeriod}
				>
					Save
				</button>
			</div>
		</Modal>
	);
};

UpdateEnrollmentModal.propTypes = {
	isOpen: PropTypes.bool,
	toggle: PropTypes.func.isRequired,
	title: PropTypes.string,
	client: PropTypes.shape({
		name: PropTypes.string,
	}),
	groups: PropTypes.arrayOf(
		PropTypes.shape({
			programId: PropTypes.number,
			groupName: PropTypes.string,
			enrollments: PropTypes.array,
		}),
	),
	onSave: PropTypes.func.isRequired,
};

UpdateEnrollmentModal.defaultProps = {
	client: {},
	groups: [],
};

export default UpdateEnrollmentModal;
